import React, { useState, useEffect, useRef } from 'react';
import { BoutiqueItems } from '../types';
import Form from 'react-bootstrap/Form';

interface ItemAutocompleteProps {
    items: BoutiqueItems[];
    value: string;
    onChange: (itemId: string, itemName: string) => void;
    placeholder?: string;
    required?: boolean;
}

const ItemAutocomplete: React.FC<ItemAutocompleteProps> = ({
    items,
    value,
    onChange,
    placeholder = "Search for an item...",
    required = false
}) => {
    const [search, setSearch] = useState('');
    const [showOptions, setShowOptions] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const prevValueRef = useRef(value);

    // Filter items based on search
    const filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(search.toLowerCase())
    );

    // Close options when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setShowOptions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Sync display with controlled value; clear input when parent clears selection (e.g. after add)
    useEffect(() => {
        const selectedItem = items.find(item => item.boutiqueItemId.toString() === value);
        if (selectedItem) {
            setSearch(selectedItem.name);
        } else if (value === '' && prevValueRef.current !== '') {
            setSearch('');
            setShowOptions(false);
        }
        prevValueRef.current = value;
    }, [value, items]);

    return (
        <div ref={searchRef} className="position-relative">
            <Form.Control
                type="text"
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setShowOptions(true);
                }}
                onFocus={() => setShowOptions(true)}
                placeholder={placeholder}
                required={required}
            />
            {showOptions && search && (
                <div 
                    className="position-absolute w-100 bg-white border rounded-bottom shadow-sm" 
                    style={{ 
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto'
                    }}
                >
                    {filteredItems.length > 0 ? (
                        filteredItems.map((item) => (
                            <div
                                key={item.boutiqueItemId}
                                className="p-2 hover-bg-light cursor-pointer"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    onChange(item.boutiqueItemId.toString(), item.name);
                                    setSearch(item.name);
                                    setShowOptions(false);
                                }}
                            >
                                {item.name}
                            </div>
                        ))
                    ) : (
                        <div className="p-2 text-muted">No items found</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ItemAutocomplete; 